/**
 * 操作ログ一覧の summary_json 表示ロジック（設計_アンケート/aaa + operation-log-fe-implementation-plan 準拠）。
 * kind 優先（operation > target > field）、operation_type / target_type / action から表示を組み立てる。
 */

import type { OperationLogSummaryJson, OperationLogSummaryJsonLegacy, OperationLogBatchSummaryType } from '@/models/operation-log'

function isNewSummaryJson(sj: OperationLogSummaryJson | OperationLogSummaryJsonLegacy | null | undefined): sj is OperationLogSummaryJson {
  return typeof sj === 'object' && sj != null && 'kind' in sj && (sj.kind === 'operation' || sj.kind === 'target' || sj.kind === 'field')
}

/** action → 表示ラベル（日本語）。i18n は t('OperationLog.action.add') 等で上書き */
export const ACTION_LABEL_MAP_JA: Record<string, string> = {
  add: '追加',
  register: '登録',
  update: '変更',
  delete: '削除',
  create: '作成',
  execute: '実行',
}

const RESERVATION_STATUS_TARGET_NAMES = new Set(['予約', 'チェックイン', '滞在中', 'チェックアウト'])

function normalizeTargetNames(targetNames: unknown): string[] {
  if (Array.isArray(targetNames)) return targetNames.map(v => String(v ?? '').trim()).filter(Boolean)
  if (targetNames == null) return []
  const single = String(targetNames).trim()
  return single ? [single] : []
}

function extractReservationStatusFromBuiltTarget(target: string): string | null {
  const m = /^予約（(.+)）$/.exec(String(target ?? '').trim())
  if (!m) return null
  const name = String(m[1] ?? '').trim()
  return RESERVATION_STATUS_TARGET_NAMES.has(name) ? name : null
}

function getReservationStatusTargetName(targetType: string, targetNames: unknown, builtTarget?: string): string | null {
  if (String(targetType ?? '').trim().toLowerCase() !== 'reservation') return null
  const name = normalizeTargetNames(targetNames)[0] ?? ''
  if (RESERVATION_STATUS_TARGET_NAMES.has(name)) return name
  if (builtTarget != null) return extractReservationStatusFromBuiltTarget(builtTarget)
  return null
}

function buildReservationStatusSummary(
  targetName: string,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  const updateKey = 'OperationLog.action.update'
  const updateLabel = t(updateKey) !== updateKey ? t(updateKey) : ACTION_LABEL_MAP_JA.update
  return `${t('OperationLog.target.reservation')}を${targetName}に${updateLabel}`
}

function normalizeReservationStatusSummaryLabel(fullLabel: string): string {
  const m = /^予約[（(](予約|チェックイン|滞在中|チェックアウト)[）)]を変更$/.exec(String(fullLabel ?? '').trim())
  if (!m) return fullLabel
  return `予約を${m[1]}に変更`
}

/** target_type に応じた target 表現を組み立て。payment は利用明細表現に統一（sales_payment 互換は持たない）。 */
export function buildTargetFromSummaryJson(
  targetType: string,
  targetNames: unknown,
  t: (key: string, opts?: Record<string, string>) => string,
): string {
  const names = normalizeTargetNames(targetNames)
  const typeKey = String(targetType ?? '').trim().toLowerCase()
  switch (typeKey) {
    case 'sales':
      return names.length ? names.join('・') : t('OperationLog.target.sales_default')
    case 'reservation':
      return names[0] ? t('OperationLog.target.reservation_with_name', { name: names[0] }) : t('OperationLog.target.reservation')
    case 'payment':
      return names.length
        ? t('OperationLog.target.payment_with_names_usage_details', { names: names.join('・') })
        : t('OperationLog.target.payment_only_usage_details')
    case 'accounts_receivable':
      return t('OperationLog.target.accounts_receivable')
    case 'checkin_guest':
      return names.length ? names.join('・') : t('OperationLog.target.checkin_guest')
    case 'reservation_guest':
      return names.length ? names.join('・') : t('OperationLog.target.reservation_guest')
    case 'room':
      return names.length ? names.join('・') : t('OperationLog.target.room')
    case 'other':
      return names.length ? names.join('・') : t('OperationLog.target.other')
    case 'csv_download':
      return names.length ? names.join('・') : t('OperationLog.module.csv_download')
    default:
      return names.length ? names.join('・') : typeKey || t('OperationLog.target.other')
  }
}

/** kind=operation のときの表示文を operation_type から組み立て */
function getOperationTypeLabel(operationType: 'checkin_execution' | 'checkout_execution', t: (key: string) => string): string {
  const key = `OperationLog.operationType.${operationType}`
  return t(key) !== key ? t(key) : operationType === 'checkin_execution' ? 'チェックイン処理を実行' : 'チェックアウト処理を実行'
}

/** 操作バッジ用: action → create | update | delete | null。execute はバッジなし。 */
export function getBadgeKindFromAction(action: string): 'create' | 'update' | 'delete' | null {
  const a = (action ?? '').toLowerCase()
  if (a === 'delete') return 'delete'
  if (a === 'update') return 'update'
  if (a === 'add' || a === 'register' || a === 'create') return 'create'
  return null
}

/** 行表示用: summary_json から「操作」列のアクションラベルと「概要」列の全文、バッジ種別を返す。 */
export function getSummaryDisplay(
  item: OperationLogBatchSummaryType,
  t: (key: string, opts?: Record<string, string>) => string,
): {
  actionLabel: string
  fullLabel: string
  badgeKind: 'create' | 'update' | 'delete' | null
} {
  const sj = item.summary_json
  if (!sj) {
    const noLabel = t('OperationLog.noSummaryLabel')
    return { actionLabel: noLabel, fullLabel: noLabel, badgeKind: null }
  }

  if (isNewSummaryJson(sj)) {
    if (sj.kind === 'operation') {
      const names = normalizeTargetNames(sj.target_names)
      const fullLabel =
        names.length > 0 ? names.join('・') : getOperationTypeLabel(sj.operation_type, t)
      return { actionLabel: t('OperationLog.action.operation_execution'), fullLabel, badgeKind: 'update' }
    }
    const actionKey = `OperationLog.action.${sj.action}`
    const actionLabel = t(actionKey) !== actionKey ? t(actionKey) : (ACTION_LABEL_MAP_JA[sj.action] ?? sj.action)
    const target = buildTargetFromSummaryJson(sj.target_type, sj.target_names, t)
    const reservationStatusTargetName = getReservationStatusTargetName(sj.target_type, sj.target_names, target)
    const isUpdateAction = String(sj.action ?? '').trim().toLowerCase() === 'update'
    const isCsvDownload = String(sj.target_type ?? '').toLowerCase() === 'csv_download'
    const fullLabel = isCsvDownload
      ? target
      : isUpdateAction && reservationStatusTargetName != null
      ? buildReservationStatusSummary(reservationStatusTargetName, t)
      : t('OperationLog.summaryWithAction', { target, action: actionLabel })
    return { actionLabel, fullLabel: normalizeReservationStatusSummaryLabel(fullLabel), badgeKind: getBadgeKindFromAction(sj.action) }
  }

  const legacy = sj as OperationLogSummaryJsonLegacy
  const action = legacy.action ?? ''
  const actionKey = `OperationLog.action.${action}`
  const actionLabel = t(actionKey) !== actionKey ? t(actionKey) : (ACTION_LABEL_MAP_JA[action] ?? action)
  const legacyTargetType = legacy.target_type ?? ''
  const target = buildTargetFromSummaryJson(legacyTargetType, legacy.target_names, t)
  const reservationStatusTargetName = getReservationStatusTargetName(legacyTargetType, legacy.target_names, target)
  const isUpdateAction = String(action ?? '').trim().toLowerCase() === 'update'
  const fullLabel =
    legacyTargetType.toLowerCase() === 'csv_download'
      ? target
      : isUpdateAction && reservationStatusTargetName != null
      ? buildReservationStatusSummary(reservationStatusTargetName, t)
      : `${target}を${actionLabel}`
  return { actionLabel, fullLabel: normalizeReservationStatusSummaryLabel(fullLabel), badgeKind: getBadgeKindFromAction(action) }
}
